import { useQuery } from "@apollo/client"
import { parseISO } from "date-fns"
import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { formatRelativeTime } from "~/common/format-relative-time"
import { lettermemeLatestPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import Text from "~/ui/typography"

const lettermemesQuery = gql(/* GraphQL */ `
  query Lettermemes {
    lettermemes {
      nodes {
        id
        name
        urlPath
        publicDescription
        viewerIsSubscribed
        latestNewsletter {
          id
          originalImageUrl
          createdAt
        }
      }
    }
  }
`)

export const LettermemesScreen: React.FC = () => {
  const { data, error, loading } = useQuery(lettermemesQuery)

  useDocumentTitle("Lettermemes")

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!data?.lettermemes?.nodes || data.lettermemes.nodes.length === 0) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          No Recent Lettermemes
        </Text>
        <Text>The lettermemes you're looking for don't exist.</Text>
      </div>
    )
  }

  const lettermemes = data.lettermemes.nodes

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-8">
        <Text as="h1" className="text-2xl font-bold">
          Lettermemes
        </Text>
        <Text className="text-gray-400">Discover and subscribe to lettermemes</Text>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {lettermemes.map((lettermeme) => (
          <div key={lettermeme.id} className="p-6">
            <Link to={lettermemeLatestPath({ urlPath: lettermeme.urlPath })}>
              <Text as="h2" className="text-xl font-semibold">
                {lettermeme.name}
              </Text>
              {lettermeme.latestNewsletter && (
                <Text as="p" className="mb-2 text-xs italic text-gray-400">
                  Updated {formatRelativeTime(parseISO(lettermeme.latestNewsletter.createdAt))}
                </Text>
              )}
            </Link>
            {lettermeme.latestNewsletter?.originalImageUrl && (
              <Link to={lettermemeLatestPath({ urlPath: lettermeme.urlPath })}>
                <img
                  src={lettermeme.latestNewsletter.originalImageUrl}
                  className="mb-4 w-full object-cover"
                  alt={`${lettermeme.name} newsletter`}
                />
              </Link>
            )}
            <Text className="mb-4 text-gray-200">{lettermeme.publicDescription}</Text>
          </div>
        ))}
      </div>
    </div>
  )
}
